import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail,
  Settings,
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  Inbox,
  Send,
  Building2,
  User,
  Clock,
  Trash2,
  AlertCircle,
  ExternalLink,
  LogIn
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../components/ui/alert";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailSettings() {
  const [searchParams] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [authUrl, setAuthUrl] = useState("");

  // Restrict email connection management to authorized user only
  const ALLOWED_CONNECT_EMAIL = "chintan@stryderesearch.com";
  const currentUser = JSON.parse(localStorage.getItem("stryde_user") || "{}");
  const canManageConnection = (currentUser.email || "").toLowerCase() === ALLOWED_CONNECT_EMAIL;

  useEffect(() => {
    // Check for OAuth callback results
    const success = searchParams.get("success");
    const error = searchParams.get("error");
    
    if (success === "true") {
      toast.success("Microsoft 365 connected successfully!");
    } else if (error) {
      toast.error(`Connection failed: ${error}`);
    }
    
    fetchConfig();
    fetchActivities();
    fetchAuthUrl();
  }, [searchParams]);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/email/config`);
      setConfig(response.data);
    } catch (error) {
      console.error("Fetch config error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuthUrl = async () => {
    try {
      const response = await axios.get(`${API}/email/auth/url`);
      setAuthUrl(response.data.auth_url);
    } catch (error) {
      console.error("Fetch auth URL error:", error);
    }
  };

  const fetchActivities = async () => {
    setLoadingActivities(true);
    try {
      const response = await axios.get(`${API}/email/activities?limit=50`);
      setActivities(response.data || []);
    } catch (error) {
      console.error("Fetch activities error:", error);
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleConnect = () => {
    if (authUrl) {
      window.location.href = authUrl;
    } else {
      toast.error("Unable to get authentication URL");
    }
  };

  const handleDisconnect = async () => {
    try {
      await axios.delete(`${API}/email/config`);
      setConfig({ configured: false });
      toast.success("Email disconnected");
    } catch (error) {
      console.error("Disconnect error:", error);
      toast.error("Failed to disconnect");
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/email/sync`);
      toast.success(`Synced ${response.data.emails_synced} emails, matched ${response.data.leads_matched} leads`);
      if (response.data.leads_updated_to_responded > 0) {
        toast.info(`${response.data.leads_updated_to_responded} leads updated to "Responded"`);
      }
      fetchActivities();
    } catch (error) {
      console.error("Sync error:", error);
      const message = error.response?.data?.detail || "Email sync failed";
      toast.error(message);
      
      // If token expired, prompt to reconnect
      if (error.response?.status === 401) {
        setConfig({ ...config, expired: true });
      }
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", { 
        month: "short", 
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#2E7AD1]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#1C2A25] font-['Outfit'] tracking-tight">Email Integration</h1>
        <p className="text-slate-500 mt-1">
          Connect your Microsoft 365 to automatically track communications with leads
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Configuration */}
        <div className="lg:col-span-1 space-y-6">
          {/* Config Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Settings className="h-5 w-5 text-slate-400" />
                Microsoft 365 Connection
              </CardTitle>
              <CardDescription>
                Secure OAuth2 connection to your mailbox
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {config?.configured && !config?.expired ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-green-800">Connected</p>
                      <p className="text-xs text-green-600">{config.email}</p>
                      {config.display_name && (
                        <p className="text-xs text-green-600">{config.display_name}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Auto-sync status */}
                  <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg border border-blue-100">
                    <RefreshCw className="h-4 w-4 text-blue-600" />
                    <div className="text-xs">
                      <p className="font-medium text-blue-800">Auto-sync enabled</p>
                      <p className="text-blue-600">
                        Syncs every 15 minutes
                        {config.last_auto_sync && (
                          <> • Last: {formatDate(config.last_auto_sync)}</>
                        )}
                      </p>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleSync}
                    disabled={syncing}
                    className="w-full bg-[#2E7AD1] hover:bg-[#2563EB]"
                    data-testid="sync-emails-btn"
                  >
                    {syncing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Sync Now
                      </>
                    )}
                  </Button>
                  
                  {canManageConnection ? (
                    <Button 
                      variant="outline"
                      onClick={handleDisconnect}
                      className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid="disconnect-btn"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Disconnect Account
                    </Button>
                  ) : (
                    <p className="text-xs text-slate-400 text-center italic">
                      Disconnect / reconnect is restricted to {ALLOWED_CONNECT_EMAIL}
                    </p>
                  )}
                </div>
              ) : config?.expired ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <AlertCircle className="h-5 w-5 text-yellow-600" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Session Expired</p>
                      <p className="text-xs text-yellow-600">{canManageConnection ? "Please reconnect your account" : `Ask ${ALLOWED_CONNECT_EMAIL} to reconnect`}</p>
                    </div>
                  </div>
                  
                  {canManageConnection ? (
                    <Button 
                      onClick={handleConnect}
                      className="w-full bg-[#2E7AD1] hover:bg-[#2563EB]"
                      data-testid="reconnect-microsoft-btn"
                    >
                      <LogIn className="h-4 w-4 mr-2" />
                      Reconnect Microsoft 365
                    </Button>
                  ) : (
                    <p className="text-xs text-slate-500 text-center">
                      Reconnection is restricted to {ALLOWED_CONNECT_EMAIL}
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <Alert>
                    <Mail className="h-4 w-4" />
                    <AlertTitle>Microsoft 365 OAuth</AlertTitle>
                    <AlertDescription className="text-xs">
                      {canManageConnection
                        ? "Click below to securely connect your Microsoft 365 account. You'll be redirected to Microsoft to authorize access."
                        : `Email integration must be connected by ${ALLOWED_CONNECT_EMAIL}. Once connected, syncs run for the whole team automatically.`}
                    </AlertDescription>
                  </Alert>
                  
                  {canManageConnection ? (
                    <Button 
                      onClick={handleConnect}
                      className="w-full bg-[#0078D4] hover:bg-[#106EBE]"
                      data-testid="connect-microsoft-btn"
                    >
                      <svg className="h-4 w-4 mr-2" viewBox="0 0 23 23" fill="currentColor">
                        <path d="M0 0h11v11H0zM12 0h11v11H12zM0 12h11v11H0zM12 12h11v11H12z"/>
                      </svg>
                      Connect Microsoft 365
                    </Button>
                  ) : (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-center">
                      <p className="text-sm text-amber-800 font-medium">Restricted Access</p>
                      <p className="text-xs text-amber-700 mt-1">Only <strong>{ALLOWED_CONNECT_EMAIL}</strong> can connect the team mailbox.</p>
                    </div>
                  )}
                  
                  <p className="text-xs text-slate-500 text-center">
                    Secure connection via Microsoft OAuth2
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">How it works</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-slate-600">
              <div className="flex items-start gap-2">
                <Inbox className="h-4 w-4 mt-0.5 text-[#2E7AD1]" />
                <p>Reads emails from your inbox (last 30 days)</p>
              </div>
              <div className="flex items-start gap-2">
                <Send className="h-4 w-4 mt-0.5 text-[#2E7AD1]" />
                <p>Tracks sent emails to leads</p>
              </div>
              <div className="flex items-start gap-2">
                <User className="h-4 w-4 mt-0.5 text-[#2E7AD1]" />
                <p>Matches emails to leads by email address</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 mt-0.5 text-[#2E7AD1]" />
                <p>Auto-updates lead status to "Responded" when they reply</p>
              </div>
              <div className="flex items-start gap-2">
                <Building2 className="h-4 w-4 mt-0.5 text-[#2E7AD1]" />
                <p>Links emails to companies for easy tracking</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Activities */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Mail className="h-5 w-5 text-slate-400" />
                    Email Activity
                  </CardTitle>
                  <CardDescription>
                    Recent email communications with leads
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={fetchActivities}
                  disabled={loadingActivities}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loadingActivities ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingActivities ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="h-6 w-6 animate-spin text-[#2E7AD1]" />
                </div>
              ) : activities.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="text-lg font-medium">No email activity yet</p>
                  <p className="text-sm">Connect your email and sync to see communications</p>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {activities.map((activity) => (
                      <div 
                        key={activity.id}
                        className={`p-4 rounded-lg border ${
                          activity.direction === "inbound" 
                            ? "bg-green-50 border-green-200" 
                            : "bg-blue-50 border-blue-200"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              {activity.direction === "inbound" ? (
                                <Badge className="bg-green-100 text-green-800 text-xs">
                                  <Inbox className="h-3 w-3 mr-1" />
                                  Received
                                </Badge>
                              ) : (
                                <Badge className="bg-blue-100 text-blue-800 text-xs">
                                  <Send className="h-3 w-3 mr-1" />
                                  Sent
                                </Badge>
                              )}
                              {activity.matched_company && (
                                <Badge variant="outline" className="text-xs">
                                  <Building2 className="h-3 w-3 mr-1" />
                                  {activity.matched_company}
                                </Badge>
                              )}
                              {activity.matched_lead_id && (
                                <Badge className="bg-purple-100 text-purple-800 text-xs">
                                  <User className="h-3 w-3 mr-1" />
                                  Matched Lead
                                </Badge>
                              )}
                            </div>
                            
                            <p className="font-medium text-slate-900 truncate">
                              {activity.subject}
                            </p>
                            
                            <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                              {activity.direction === "inbound" ? (
                                <span>From: {activity.from_email}</span>
                              ) : (
                                <span>To: {activity.to_email}</span>
                              )}
                            </div>
                            
                            {activity.snippet && (
                              <p className="text-xs text-slate-600 mt-2 line-clamp-2">
                                {activity.snippet}
                              </p>
                            )}
                          </div>
                          
                          <div className="text-right text-xs text-slate-400 whitespace-nowrap">
                            <Clock className="h-3 w-3 inline mr-1" />
                            {formatDate(activity.date)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
